import { useEffect, useState, useCallback } from "react";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Nfc, Users, Building2, Loader2, ShoppingCart, QrCode, Ban, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageLoading } from "@/components/shared";
import api, { formatApiErrorDetail } from "@/lib/api";

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

export default function TimeHardware() {
  const { t } = useTranslation();
  const [acc, setAcc] = useState(undefined);
  const [cfg, setCfg] = useState(null);
  const [sites, setSites] = useState([]);
  const [preSite] = useState(() => new URLSearchParams(window.location.search).get("site") || "");
  const [orders, setOrders] = useState(null);
  const [tags, setTags] = useState(null);
  const [scope, setScope] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    site_id: "", tag_type: "STANDARD", quantity: 1,
    shipping_recipient: "", shipping_company: "", shipping_street: "",
    shipping_postal_code: "", shipping_city: "", shipping_country: "DE",
  });

  const loadOrders = useCallback(() => {
    api.get("/time/hardware/orders").then((r) => setOrders(r.data.orders || [])).catch(() => setOrders([]));
    api.get("/time/hardware/tags").then((r) => setTags(r.data.tags || [])).catch(() => setTags([]));
  }, []);

  const dlQr = async (tg) => {
    try {
      const r = await api.get(`/time/hardware/tags/${tg.id}/qr.png`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${tg.nfc_tag_id}.png`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error"); }
  };
  const disableTag = async (tg) => {
    if (!window.confirm(t("hwt.confirmDisable"))) return;
    try { await api.post(`/time/hardware/tags/${tg.id}/disable`); toast.success("✓"); loadOrders(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error"); }
  };

  useEffect(() => {
    api.get("/employer/module/TIME_TRACKING").then((r) => setAcc(r.data?.access || "none")).catch(() => setAcc("none"));
  }, []);
  useEffect(() => {
    if (acc !== "full") return;
    api.get("/time/hardware/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/time/sites").then((r) => { setSites(r.data.sites || []); if (preSite) setF((prev) => ({ ...prev, site_id: preSite })); }).catch(() => {});
    loadOrders();
  }, [acc, loadOrders]);

  useEffect(() => {
    if (!f.site_id) { setScope(null); return; }
    api.get(`/time/hardware/sites/${f.site_id}/scope-preview`).then((r) => setScope(r.data)).catch(() => setScope(null));
  }, [f.site_id]);

  if (acc === undefined || (acc === "full" && !cfg)) return <PageLoading />;
  if (acc !== "full") return <Navigate to="/app/employer/time/upgrade" replace />;

  const tagPrice = (cfg.tag_types.find((x) => x.key === f.tag_type) || {}).price || 0;
  const shipping = cfg.shipping?.amount || 0;
  const subtotal = tagPrice * (Number(f.quantity) || 0);
  const total = subtotal + shipping;
  const canPay = f.site_id && f.quantity >= 1 && f.quantity <= 20 &&
    f.shipping_recipient && f.shipping_street && f.shipping_postal_code && f.shipping_city;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/time/hardware/orders/checkout", { ...f, quantity: Number(f.quantity), origin_url: window.location.origin });
      toast.success(t("hw.redirecting"));
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
      setBusy(false);
    }
  };

  const payBadge = (s) => ({ PAID: "bg-emerald-100 text-emerald-700", PAYMENT_PENDING: "bg-amber-100 text-amber-700" }[s] || "bg-slate-100 text-slate-600");

  return (
    <div data-testid="time-hardware">
      <div className="mb-1 flex flex-wrap items-center gap-2">
        <Link to="/app/employer/time" className="flex items-center gap-1 text-sm text-slate-500 hover:text-navy" data-testid="hw-back"><ArrowLeft className="h-4 w-4" /> {t("hw.back")}</Link>
        <h1 className="ml-2 flex items-center gap-2 font-heading text-2xl font-extrabold text-navy"><Nfc className="h-6 w-6 text-cyan-dark" /> {t("hw.title")}</h1>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-slate-500">{t("hw.subtitle")}</p>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Order form */}
        <div className="lg:col-span-2 space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="hw-order-form">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-navy"><ShoppingCart className="h-5 w-5 text-cyan-dark" /> {t("hw.order")}</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">{t("hw.site")}</label>
              <select value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="hw-site">
                <option value="">{t("hw.selectSite")}</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">{t("hw.tagType")}</label>
              <select value={f.tag_type} onChange={(e) => setF({ ...f, tag_type: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="hw-tag-type">
                {cfg.tag_types.map((x) => <option key={x.key} value={x.key}>{x.key === "STANDARD" ? t("hw.standard") : t("hw.outdoor")} — {eur(x.price)}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">{t("hw.quantity")} (1–20)</label>
              <Input type="number" min={1} max={20} value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="hw-qty" />
            </div>
          </div>

          {/* Scope preview */}
          {f.site_id && (
            <div className="rounded-xl border border-slate-100 bg-slate-50 p-4" data-testid="hw-scope">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("hw.scopeTitle")}</div>
              {scope && (scope.internal_workers > 0 || scope.subcontractors.length > 0) ? (
                <div className="space-y-1 text-sm text-slate-600">
                  <div className="flex items-center gap-2"><Users className="h-4 w-4 text-cyan-dark" /> {scope.internal_workers} {t("hw.internalWorkers")}</div>
                  {scope.subcontractors.map((s) => (
                    <div key={s.subcontractor_id} className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /> {s.name} — {s.eligible_workers} {t("hw.eligibleWorkers")}</div>
                  ))}
                </div>
              ) : <div className="text-sm text-slate-400">{t("hw.noScope")}</div>}
            </div>
          )}

          {/* Shipping */}
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("hw.shippingAddr")}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder={t("hw.recipient")} value={f.shipping_recipient} onChange={(e) => setF({ ...f, shipping_recipient: e.target.value })} data-testid="hw-recipient" />
              <Input placeholder={t("hw.company")} value={f.shipping_company} onChange={(e) => setF({ ...f, shipping_company: e.target.value })} data-testid="hw-company" />
              <Input placeholder={t("hw.street")} value={f.shipping_street} onChange={(e) => setF({ ...f, shipping_street: e.target.value })} className="sm:col-span-2" data-testid="hw-street" />
              <Input placeholder={t("hw.postal")} value={f.shipping_postal_code} onChange={(e) => setF({ ...f, shipping_postal_code: e.target.value })} data-testid="hw-postal" />
              <Input placeholder={t("hw.city")} value={f.shipping_city} onChange={(e) => setF({ ...f, shipping_city: e.target.value })} data-testid="hw-city" />
              <Input placeholder={t("hw.country")} value={f.shipping_country} onChange={(e) => setF({ ...f, shipping_country: e.target.value })} data-testid="hw-country" />
            </div>
          </div>
        </div>

        {/* Summary */}
        <div className="h-fit space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="hw-summary">
          <h2 className="font-heading text-lg font-bold text-navy">{t("hw.summary")}</h2>
          <div className="flex justify-between text-sm text-slate-600"><span>{f.quantity || 0} × {t("hw.tags")}</span><span data-testid="hw-subtotal">{eur(subtotal)}</span></div>
          <div className="flex justify-between text-sm text-slate-600"><span>{t("hw.shipping")}</span><span data-testid="hw-shipping">{eur(shipping)}</span></div>
          <div className="flex justify-between border-t border-slate-100 pt-2 font-heading text-lg font-extrabold text-navy"><span>{t("hw.total")}</span><span data-testid="hw-total">{eur(total)}</span></div>
          <p className="text-xs text-slate-400">{t("hw.shipOnce")}</p>
          <Button onClick={submit} disabled={!canPay || busy} className="w-full rounded-full bg-navy text-white hover:bg-navy/90" data-testid="hw-pay">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ShoppingCart className="mr-1 h-4 w-4" />} {t("hw.payOrder")}
          </Button>
          <p className="text-[11px] text-slate-400">{t("hw.taxNote")}</p>
        </div>
      </div>

      {/* Your tags */}
      {tags && tags.length > 0 && (
        <>
          <h2 className="mb-3 mt-8 flex items-center gap-2 font-heading text-lg font-bold text-navy"><Nfc className="h-5 w-5 text-cyan-dark" /> {t("hwt.yourTags")}</h2>
          <div className="grid gap-3 sm:grid-cols-2" data-testid="hw-tags">
            {tags.map((tg) => (
              <div key={tg.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`hw-tag-${tg.nfc_tag_id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold text-navy">{tg.nfc_tag_id}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tg.status === "ACTIVE" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>{tg.status}</span>
                  <span className="text-[11px] text-slate-400">{tg.tag_type === "OUTDOOR_ANTI_METAL" ? t("hw.outdoor") : t("hw.standard")}</span>
                </div>
                <div className="mt-2 space-y-0.5 text-sm text-slate-600">
                  <div>{t("hwt.assigned")}: <span className="font-medium text-navy">{tg.site_name}</span></div>
                  <div className="text-xs text-slate-500">{t("hwt.authorizedWorkforce")}: {tg.internal_workers} {t("hw.internalWorkers")}{tg.subcontractors.map((s) => `, ${s.name} (${s.eligible_workers})`).join("")}</div>
                  <div className="text-xs text-slate-400">{t("hwt.lastUsed")}: {tg.last_used_at ? new Date(tg.last_used_at).toLocaleString() : t("hwt.never")}</div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => dlQr(tg)} data-testid={`hw-tag-qr-${tg.nfc_tag_id}`}><QrCode className="mr-1 h-3.5 w-3.5" /> {t("hwt.viewQr")}</Button>
                  <Link to="/app/employer/time/access"><Button size="sm" variant="outline" className="rounded-full" data-testid={`hw-tag-access-${tg.nfc_tag_id}`}><KeyRound className="mr-1 h-3.5 w-3.5" /> {t("hwt.manageAccess")}</Button></Link>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => disableTag(tg)} data-testid={`hw-tag-disable-${tg.nfc_tag_id}`}><Ban className="mr-1 h-3.5 w-3.5" /> {t("hwt.disable")}</Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Orders */}
      <h2 className="mb-3 mt-8 font-heading text-lg font-bold text-navy">{t("hw.ordersTitle")}</h2>
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="hw-orders">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{["colOrder", "colSite", "colQty", "colTotal", "colStatus", "colFulfil", "colDate"].map((c) => <th key={c} className="px-4 py-3">{t(`hw.${c}`)}</th>)}</tr>
          </thead>
          <tbody>
            {!orders ? <tr><td colSpan={7} className="p-8 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr> :
              orders.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-slate-400">{t("hw.empty")}</td></tr> :
                orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-50" data-testid={`hw-order-${o.order_number}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-navy">{o.order_number}</td>
                    <td className="px-4 py-3 text-slate-600">{o.site_name}</td>
                    <td className="px-4 py-3 text-slate-600">{o.quantity}</td>
                    <td className="px-4 py-3 font-medium text-navy">{eur(o.total)}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${payBadge(o.payment_status)}`}>{o.payment_status}</span></td>
                    <td className="px-4 py-3 text-slate-500">{o.fulfillment_status}</td>
                    <td className="px-4 py-3 text-slate-500">{(o.ordered_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
